/* eslint-disable import/no-default-export */
import { Storage } from '@trello/storage';
import {
  LOAD_NOTIFICATION_GROUPS_ERROR,
  LOAD_NOTIFICATION_GROUPS_REQUEST,
  LOAD_NOTIFICATION_GROUPS_SUCCESS,
  LOAD_UNREAD_NOTIFICATIONS_ERROR,
  LOAD_UNREAD_NOTIFICATIONS_REQUEST,
  LOAD_UNREAD_NOTIFICATIONS_SUCCESS,
  loadNotificationGroups,
  LoadNotificationGroupsRequestAction,
  LoadNotificationGroupsSuccessAction,
  LoadUnreadNotificationsRequestAction,
  LoadUnreadNotificationsSuccessAction,
} from 'app/gamma/src/modules/loaders/load-notifications';
import { resetNotificationGroups } from 'app/gamma/src/modules/state/models/notifications';
import { StandardThunkAction, Dispatch } from 'app/gamma/src/types';
import { Action, actionCreator, createReducer } from '@trello/redux';
import {
  getNotificationFilterStateKey,
  isNotificationFilterVisibilityUnread,
  NotificationFilter,
  setNotificationVisibilityFilter,
} from 'app/gamma/src/util/application-storage/notification-filter';
import { State } from 'app/gamma/src/modules/types';

// Action types

const SET_NOTIFICATION_FILTERING_BY_UNREAD = Symbol(
  'SET_NOTIFICATION_FILTERING_BY_UNREAD',
);

// Actions Creators

type SetNotificationFilteringByUnreadAction = Action<
  typeof SET_NOTIFICATION_FILTERING_BY_UNREAD,
  boolean
>;

export const setNotificationFilteringByUnreadAction = actionCreator(
  SET_NOTIFICATION_FILTERING_BY_UNREAD,
);

// Reducer
export interface NotificationsPaneState {
  isFilteringByUnread: boolean;
  loading: number;
  loadingUnread: number;
  moreToLoad: boolean;
  moreUnreadToLoad: boolean;
}

const initialState: NotificationsPaneState = {
  isFilteringByUnread: isNotificationFilterVisibilityUnread(),
  loading: 0,
  loadingUnread: 0,
  moreToLoad: true,
  moreUnreadToLoad: true,
};

export default createReducer(initialState, {
  [LOAD_NOTIFICATION_GROUPS_REQUEST](
    state,
    { payload }: LoadNotificationGroupsRequestAction,
  ) {
    return {
      ...state,
      loading: payload.limit,
    };
  },

  [LOAD_NOTIFICATION_GROUPS_SUCCESS](
    state,
    { payload }: LoadNotificationGroupsSuccessAction,
  ) {
    return {
      ...state,
      loading: 0,
      moreToLoad:
        state.loading - payload.notificationGroupsResponse.length === 0,
    };
  },

  [LOAD_NOTIFICATION_GROUPS_ERROR](state) {
    return {
      ...state,
      loading: 0,
    };
  },

  [LOAD_UNREAD_NOTIFICATIONS_REQUEST](
    state,
    { payload }: LoadUnreadNotificationsRequestAction,
  ) {
    return {
      ...state,
      loadingUnread: payload.limit,
    };
  },

  [LOAD_UNREAD_NOTIFICATIONS_SUCCESS](
    state,
    { payload }: LoadUnreadNotificationsSuccessAction,
  ) {
    return {
      ...state,
      loadingUnread: 0,
      moreUnreadToLoad:
        state.loadingUnread - payload.partialUnreadNotifications.length === 0,
    };
  },

  [LOAD_UNREAD_NOTIFICATIONS_ERROR](state) {
    return {
      ...state,
      loadingUnread: 0,
    };
  },

  [SET_NOTIFICATION_FILTERING_BY_UNREAD](
    state,
    { payload: isFilteringByUnread }: SetNotificationFilteringByUnreadAction,
  ) {
    return {
      ...state,
      isFilteringByUnread,
    };
  },
});

export const setFilterAndLoadNotifications = (
  isFilteringByUnread: boolean,
): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    dispatch(resetNotificationGroups());
    dispatch(setNotificationFilteringByUnreadAction(isFilteringByUnread));
    dispatch(loadNotificationGroups());
  };
};

Storage.listenSyncedAcrossBrowser(({ key }, dispatch: Dispatch) => {
  if (dispatch && key === getNotificationFilterStateKey()) {
    dispatch(
      setFilterAndLoadNotifications(isNotificationFilterVisibilityUnread()),
    );
  }
});

export const filterByUnreadNotifications = (): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    if (Storage.isEnabled()) {
      setNotificationVisibilityFilter(NotificationFilter.VISIBILITY_UNREAD);
    } else {
      dispatch(setFilterAndLoadNotifications(true));
    }
  };
};

export const viewAllNotifications = (): StandardThunkAction => {
  return async (dispatch: Dispatch, getState: () => State) => {
    if (Storage.isEnabled()) {
      setNotificationVisibilityFilter(NotificationFilter.VISIBILITY_ALL);
    } else {
      dispatch(setFilterAndLoadNotifications(false));
    }
  };
};
